import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Outlet, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { publicApi, ApiError, type Storefront } from '../lib/public-api';
import { TenantTheme } from '../lib/tenant-theme';
import { ensureTenantHead, registerTenantWorker, tenantBase } from '../lib/pwa';
import { Center } from '@astryxdesign/core/Center';
import { Spinner } from '@astryxdesign/core/Spinner';
import { ErrorView } from '../components/StateViews';
import { isConfigured } from '../lib/config';
import { SLUG_RE } from '../../supabase/functions/_shared/domain/business-schema.ts';
import { BookingSheet } from '../features/storefront/BookingSheet';
import { useToast } from '@astryxdesign/core/Toast';

interface TenantCtx {
  slug: string;
  base: string;
  sf: Storefront;
  openBooking: (serviceId?: string) => void;
}

const Ctx = createContext<TenantCtx | null>(null);
const PortalCtx = createContext<HTMLElement | null>(null);
/** Overlay mount point inside the studio theme (portals into <body> would lose the studio accent). */
export const usePortalContainer = () => useContext(PortalCtx);

export function useTenant(): TenantCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTenant outside TenantRoot');
  return v;
}

export const storefrontKey = (slug: string) => ['storefront', slug] as const;

function NotConfigured() {
  return (
    <Center height="100dvh">
      <ErrorView error={new Error('Приложение не настроено: задайте VITE_SUPABASE_URL и VITE_SUPABASE_ANON_KEY (см. SETUP.md).')} title="Нет настроек" />
    </Center>
  );
}

export function TenantRoot() {
  const { slug = '' } = useParams();
  const valid = SLUG_RE.test(slug);
  const q = useQuery({
    queryKey: storefrontKey(slug),
    queryFn: () => publicApi.storefront(slug),
    enabled: valid && isConfigured,
    staleTime: 30_000,
    retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 2,
  });

  useEffect(() => {
    if (!q.data) return;
    ensureTenantHead(slug, q.data.profile.name, q.data.profile.accent_color);
    void registerTenantWorker(slug);
  }, [q.data, slug]);

  if (!isConfigured) return <NotConfigured />;
  if (!valid || (q.error instanceof ApiError && q.error.status === 404)) {
    return (
      <Center height="100dvh">
        <ErrorView error={new Error('Проверьте ссылку: такой студии нет или она ещё не опубликована.')} title="Студия не найдена" />
      </Center>
    );
  }
  if (q.isPending) {
    return (
      <Center height="100dvh">
        <Spinner size="lg" label="Загружаем студию" />
      </Center>
    );
  }
  if (q.isError) {
    return (
      <Center height="100dvh">
        <ErrorView error={q.error} onRetry={() => q.refetch()} />
      </Center>
    );
  }
  return <TenantProvider slug={slug} sf={q.data} />;
}

function TenantProvider({ slug, sf }: { slug: string; sf: Storefront }) {
  const [portal, setPortal] = useState<HTMLElement | null>(null);
  return (
    <TenantTheme accent={sf.profile.accent_color}>
      <PortalCtx.Provider value={portal}>
        <BookingHost slug={slug} sf={sf}>
          <Outlet />
        </BookingHost>
      </PortalCtx.Provider>
      <div ref={setPortal} data-overlay-root="" />
    </TenantTheme>
  );
}

function BookingHost({ slug, sf, children }: { slug: string; sf: Storefront; children: ReactNode }) {
  const [booking, setBooking] = useState<{ open: boolean; serviceId?: string }>({ open: false });
  const showToast = useToast();
  const value = useMemo<TenantCtx>(() => ({
    slug, base: tenantBase(slug), sf,
    openBooking: (serviceId?: string) => setBooking({ open: true, serviceId }),
  }), [slug, sf]);
  useEffect(() => {
    const off = () => showToast({ body: 'Нет соединения. Записаться получится, когда связь вернётся.', type: 'error', uniqueID: 'offline' });
    window.addEventListener('offline', off);
    return () => window.removeEventListener('offline', off);
  }, [showToast]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <BookingSheet
        open={booking.open}
        initialServiceId={booking.serviceId}
        onClose={() => setBooking({ open: false })}
      />
    </Ctx.Provider>
  );
}
